import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pencil, X, Eraser, Trash2 } from 'lucide-react';

/**
 * Translucent Scratchpad overlay that slides from the right edge.
 * Drawing is local & ephemeral (cleared on close).
 */
export default function Scratchpad({ open, onClose }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [tool, setTool] = useState('pen');

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [open]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { drawingRef.current = true; lastRef.current = pos(e); };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.strokeStyle = tool === 'pen' ? 'rgba(199,210,254,0.95)' : 'rgba(11,15,26,1)';
    ctx.lineWidth = tool === 'pen' ? 2 : 14;
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };
  const end = () => { drawingRef.current = false; };
  const clearAll = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 280, damping: 32 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md z-50 flex flex-col"
          >
            <div className="relative h-full m-3 rounded-2xl border border-white/10 bg-[#0b0f1a]/85 backdrop-blur-xl shadow-2xl overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 h-12 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-indigo-300" />
                  <span className="text-[12.5px] font-medium text-slate-200 tracking-wide">Scratchpad</span>
                </div>
                <div className="flex items-center gap-1">
                  <ToolButton active={tool === 'pen'} onClick={() => setTool('pen')} label="Pen" icon={<Pencil className="w-3.5 h-3.5" />} />
                  <ToolButton active={tool === 'eraser'} onClick={() => setTool('eraser')} label="Erase" icon={<Eraser className="w-3.5 h-3.5" />} />
                  <button onClick={clearAll} title="Clear" className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-white/5"><Trash2 className="w-3.5 h-3.5" /></button>
                  <button onClick={onClose} title="Close" className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-white/5"><X className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="relative flex-1">
                <div className="absolute inset-0 opacity-[0.07]"
                  style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
                <canvas
                  ref={canvasRef}
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                  className="relative w-full h-full touch-none cursor-crosshair"
                />
              </div>
              <div className="px-4 py-2 text-[11px] text-slate-500 border-t border-white/5">
                Sketch algebraic steps or geometric proofs. Cleared when you close.
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function ToolButton({ active, onClick, label, icon }) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`flex items-center gap-1 px-2 h-7 rounded-md text-[11.5px] ${active ? 'bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/30' : 'text-slate-400 hover:text-slate-100 hover:bg-white/5'}`}
    >
      {icon}{label}
    </button>
  );
}
